/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import {
  Dimmer,
  DimmerDimmable,
  Header,
  Icon,
  Loader,
  Segment,
  Table,
} from '@mairistem/composants';

import type { Acte, TypeActe } from '@/types';

import ActeTableLine from './ActeTableLine';

interface Props {
  /**
   * Actes to display
   */
  actes: Array<Acte>;
  /**
   * Boolean to know if loading finished
   */
  loading: boolean;
  /**
   * Called when a row is clicked.
   *
   * The identifiant is the identifiant of the acte clicked.
   */
  onSelected?: (acte: Acte) => void;
  /**
   * The type of acte to display.
   */
  typeActe: TypeActe;
}

const ActeTable = (props: Props) => {
  const { actes, loading, onSelected, typeActe } = props;

  let headers: () => React.ReactNode;

  switch (typeActe) {
    case 'naissance':
      headers = () => (
        <>
          <Table.HeaderCell content="Date d'événement" />
          <Table.HeaderCell content="Enfant" />
          <Table.HeaderCell content="Parents" />
        </>
      );
      break;
    case 'reconnaissance':
      headers = () => (
        <>
          <Table.HeaderCell content="Enfant" />
          <Table.HeaderCell content="Reconnue par le père" />
          <Table.HeaderCell content="Reconnue par la mère" />
        </>
      );
      break;
    case 'mariage':
      headers = () => (
        <>
          <Table.HeaderCell content="Date d'événement" />
          <Table.HeaderCell>
            1<sup>er</sup> conjoint
          </Table.HeaderCell>
          <Table.HeaderCell>
            2<sup>ème</sup> conjoint
          </Table.HeaderCell>
        </>
      );
      break;
    case 'deces':
      headers = () => (
        <>
          <Table.HeaderCell content="Date d'événement" />
          <Table.HeaderCell content="Défunt" />
          <Table.HeaderCell content="Parents" />
        </>
      );
      break;
    default:
      headers = () => undefined;
      break;
  }

  return _.isEmpty(actes) && !loading ? (
    <Segment placeholder style={{ height: '100%' }}>
      <Header icon>
        <Icon name="search" />
        Aucun acte à afficher
      </Header>
    </Segment>
  ) : (
    <Table celled selectable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell content="Année" />
          <Table.HeaderCell content="N° acte" />
          <Table.HeaderCell content="Date et heure dressé" />
          {headers()}
        </Table.Row>
      </Table.Header>
      <DimmerDimmable
        as={Table.Body}
        dimmed={loading}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(loading && { style: { height: 100 } })}
      >
        <Dimmer active={loading} inverted>
          <Loader indeterminate />
        </Dimmer>
        {_.map(actes, (acte) => (
          <ActeTableLine
            key={acte.identifiant}
            typeActe={typeActe}
            acte={acte}
            onSelected={onSelected}
          />
        ))}
      </DimmerDimmable>
    </Table>
  );
};

export default ActeTable;
